/**
 * validates UEN of businesses in Singapore
 * https://www.uen.gov.sg/ueninternet/faces/pages/admin/aboutUEN.jspx
 * @param {string} uen
 * @returns {boolean}
 */
// NOSONAR
export function validateUEN (uen: string): boolean { // NOSONAR
  let debug = true;
  const entityTypeIndicator = [
    'LP', 'LL', 'FC', 'PF', 'RF', 'MQ', 'MM', 'NB', 'CC', 'CS', 'MB', 'FM', 'GS', 'GA',
    'GB', 'DP', 'CP', 'NR', 'CM', 'CD', 'MD', 'HS', 'VH', 'CH', 'MH', 'CL', 'XL', 'CX',
    'RP', 'TU', 'TC', 'FB', 'FN', 'PA', 'PB', 'SS', 'MC', 'SM'
  ];

  /**
   * Validates NRIC/FIN format in Singapore.
   * NRIC/FIN starts with S, T, F, G, or M followed by 7 digits and an uppercase letter.
   * Does not validate the checksum.
   *
   * Examples of valid NRIC/FIN: S1234567D, T7654321Z, F2345678A, G9876543B
   *
   * @param {string} uen - The NRIC or FIN to validate
   * @param {boolean} [debug] - Optional flag to log validation result
   */

  const nricRegex = /^[STFGM]\d{7}[A-Z]$/;
  if (nricRegex.test(uen)) {
    return true;
  }

  // check that uen is not empty
  if (!uen || String(uen) === '') {
    return false;
  }

  // check if uen is 9 or 10 digits
  if (uen.length < 9 || uen.length > 10) {
    return false;
  }

  uen = uen.toUpperCase();
  let uenStrArray = uen.split('');

  // (A) Businesses registered with ACRA
  if (uenStrArray.length === 9) {
    // check that last character is a letter
    if (!Number.isNaN(Number(uenStrArray.at(-1)))) {
      return false;
    }
    for (const value of uenStrArray.slice(0, -1)) {
      // check that first 8 letters are all numbers
      if (Number.isNaN(Number(value))) {
        return false;
      }
    }

    // (A) Businesses registered with ACRA (SUCCESS)
    return true;
  }
  else if (uenStrArray.length === 10) {
    // check that last character is a letter
    if (!Number.isNaN(Number(uenStrArray.at(-1)))) {
      return false;
    }

    // (B) Local companies registered with ACRA
    if (!Number.isNaN(Number(uenStrArray[0])) && !Number.isNaN(Number(uenStrArray[1])) && !Number.isNaN(Number(uenStrArray[2])) && !Number.isNaN(Number(uenStrArray[3]))) {
      // check that 5th to 9th letters are all numbers
      if (!Number.isNaN(Number(uenStrArray[4])) && !Number.isNaN(Number(uenStrArray[5])) && !Number.isNaN(Number(uenStrArray[6])) &&
        !Number.isNaN(Number(uenStrArray[7])) && !Number.isNaN(Number(uenStrArray[8]))) {
        // (B) Local companies registered with ACRA (SUCCESS)
        return true;
      } else {
        return false;
      }
    }
    // (C) All other entities which will be issued new UEN
    else {
      // check that 1st letter is either T or S or R
      if (uenStrArray[0] !== 'T' && uenStrArray[0] !== 'S' && uenStrArray[0] !== 'R') {
        return false;
      }

      // check that 2nd and 3rd letters are numbers only
      if (Number.isNaN(Number(uenStrArray[1])) || Number.isNaN(Number(uenStrArray[2]))) {
        return false;
      }

      // check that 4th letter is an alphabet
      if (!Number.isNaN(Number(uenStrArray[3]))) {
        return false;
      }

      // check entity-type indicator
      let entityTypeMatch = false,
        entityType = String(uenStrArray[3]) + String(uenStrArray[4]);
      for (const type of entityTypeIndicator) {
        if (String(type) === String(entityType)) {
          entityTypeMatch = true;
          break;
        }
      }
      if (!entityTypeMatch) {
        return false;
      }

      // check that 6th to 9th letters are numbers only
      if (Number.isNaN(Number(uenStrArray[5])) || Number.isNaN(Number(uenStrArray[6])) || Number.isNaN(Number(uenStrArray[7])) || Number.isNaN(Number(uenStrArray[8]))) {
        return false;
      }

      // (C) All other entities which will be issued new UEN (SUCCESS)
      return true;
    }
  }

  return false;
}
